/**
 * The persistent numbered step rail (f10.3), following the prototype's
 * `.step-rail` markup in `cycle-workflow.html`.
 *
 * Every step is a real link, never disabled: the rail is a guide, not a gate
 * (BR-F10.3-3). The server decides status and the current step; this renders.
 */

import { Link } from 'react-router-dom';
import { Check, RefreshCw } from 'lucide-react';

import type { CycleWorkflow } from '@/api/client';

import { railClassFor, stepMeta } from './workflowView';

export function StepRail({
  workflow,
  cycleId,
  openStepCode,
}: {
  workflow: CycleWorkflow;
  cycleId: string;
  /** The step whose route is open, marked `aria-current="page"`. */
  openStepCode: string | null;
}) {
  return (
    <div className="card cycle-rail-card">
      <div className="card-head">
        <h3>Preparation</h3>
        <span className="hint">
          {workflow.completed_count} of {workflow.steps.length}
        </span>
      </div>
      <div className="card-body cycle-rail-body">
        <nav className="step-rail cycle-rail" aria-label="Preparation steps">
          {workflow.steps.map((step) => {
            const railClass = railClassFor(step, workflow.current_step_code);
            return (
              <Link
                key={step.code}
                to={`/cycles/${cycleId}/steps/${step.code}`}
                className={`step ${railClass}`}
                aria-current={step.code === openStepCode ? 'page' : undefined}
                data-step={step.code}
              >
                <span className="step-num" aria-hidden="true">
                  {railClass === 'is-done' ? (
                    <Check />
                  ) : railClass === 'is-stale' ? (
                    <RefreshCw />
                  ) : (
                    step.number
                  )}
                </span>
                <span className="step-body">
                  <span className="step-title">
                    {step.name}
                    {railClass === 'is-stale' && (
                      <span className="badge pill-warning">Updates available</span>
                    )}
                  </span>
                  <span className="step-meta">{stepMeta(step, workflow)}</span>
                </span>
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="card-foot">
        <span className="hint">
          Steps are numbered, not blocking — revisit any of them freely.
        </span>
      </div>
    </div>
  );
}
